/**
 * Knowledge Assets API service
 *
 * Handles CRUD and processing for org-level knowledge assets
 * (PDFs, videos, URLs) managed on the Background page.
 */

import apiClient, { ApiResponse } from './client';

// ========================================
// INTERFACES
// ========================================

export type KnowledgeAssetFileType = 'pdf' | 'video' | 'markdown' | 'text';
export type KnowledgeAssetType = KnowledgeAssetFileType | 'url';
export type KnowledgeAssetUrlType = 'landing_page' | 'documentation';

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'avi', 'mkv'];
const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo', 'video/x-matroska'];
const MARKDOWN_EXTENSIONS = ['md', 'markdown'];
const MARKDOWN_MIME_TYPES = ['text/markdown', 'text/x-markdown'];
const TEXT_EXTENSIONS = ['txt'];
const TEXT_MIME_TYPES = ['text/plain'];

export function detectKnowledgeAssetFileType(file: File): KnowledgeAssetFileType | null {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const mimeType = file.type.toLowerCase();

  if (ext === 'pdf' || mimeType === 'application/pdf') {
    return 'pdf';
  }
  if (MARKDOWN_EXTENSIONS.includes(ext) || MARKDOWN_MIME_TYPES.includes(mimeType)) {
    return 'markdown';
  }
  if (TEXT_EXTENSIONS.includes(ext) || TEXT_MIME_TYPES.includes(mimeType)) {
    return 'text';
  }
  if (VIDEO_EXTENSIONS.includes(ext) || VIDEO_MIME_TYPES.includes(mimeType)) {
    return 'video';
  }
  return null;
}

export function isDocumentKnowledgeAssetType(type: string): type is Exclude<KnowledgeAssetFileType, 'video'> {
  return type === 'pdf' || type === 'markdown' || type === 'text';
}

export function getKnowledgeAssetTypeLabel(
  assetType: KnowledgeAssetType,
  urlType?: KnowledgeAssetUrlType | null
): string {
  if (assetType === 'url') {
    if (urlType === 'landing_page') return 'URL - Landing Page';
    if (urlType === 'documentation') return 'URL - Documentation';
    return 'URL';
  }
  if (assetType === 'pdf') return 'PDF';
  if (assetType === 'markdown') return 'Markdown';
  if (assetType === 'text') return 'Text';
  return 'Video';
}

export interface KnowledgeAsset {
  id: string;
  organization_id: string;
  asset_type: KnowledgeAssetType;
  title: string | null;
  filename: string | null;
  url: string | null;
  url_type: KnowledgeAssetUrlType | null;
  file_size_bytes: number | null;
  summary: string | null;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_error: string | null;
  facets: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  processed_at: string | null;
}

export interface KnowledgeAssetListResponse {
  assets: KnowledgeAsset[];
  total_count: number;
}

// ========================================
// SERVICE CLASS
// ========================================

class KnowledgeAssetService {
  private baseEndpoint = '/api/knowledge-assets';

  /**
   * Request a signed upload URL for a knowledge asset file
   */
  async requestUploadUrl(
    organizationId: string,
    filename: string,
    fileType: KnowledgeAssetFileType
  ): Promise<ApiResponse<{ storage_path: string; upload_url: string }>> {
    return apiClient.post<{ storage_path: string; upload_url: string }>(
      `${this.baseEndpoint}/upload-url`,
      {
        organization_id: organizationId,
        filename,
        file_type: fileType,
      }
    );
  }

  /**
   * Upload file to signed URL (direct to storage)
   */
  async uploadToSignedUrl(uploadUrl: string, file: File): Promise<boolean> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Failed to upload file:', error);
      return false;
    }
  }

  /**
   * Create a file-based knowledge asset (after upload to signed URL)
   */
  async createFileAsset(
    organizationId: string,
    storagePath: string,
    filename: string,
    fileType: KnowledgeAssetFileType,
    title?: string,
    fileSizeBytes?: number
  ): Promise<ApiResponse<KnowledgeAsset>> {
    return apiClient.post<KnowledgeAsset>(this.baseEndpoint, {
      organization_id: organizationId,
      storage_path: storagePath,
      filename,
      file_type: fileType,
      title: title || null,
      file_size_bytes: fileSizeBytes || null,
    });
  }

  /**
   * Create a URL-based knowledge asset
   */
  async createUrlAsset(
    organizationId: string,
    url: string,
    title?: string,
    urlType: KnowledgeAssetUrlType = 'landing_page'
  ): Promise<ApiResponse<KnowledgeAsset>> {
    return apiClient.post<KnowledgeAsset>(`${this.baseEndpoint}/url`, {
      organization_id: organizationId,
      url,
      title: title || null,
      url_type: urlType,
    });
  }

  /**
   * List all knowledge assets for an organization
   */
  async listAssets(organizationId: string): Promise<ApiResponse<KnowledgeAssetListResponse>> {
    return apiClient.get<KnowledgeAssetListResponse>(this.baseEndpoint, {
      organization_id: organizationId,
    });
  }

  /**
   * Get a single knowledge asset
   */
  async getAsset(assetId: string): Promise<ApiResponse<KnowledgeAsset>> {
    return apiClient.get<KnowledgeAsset>(`${this.baseEndpoint}/${assetId}`);
  }

  /**
   * Update asset title or summary
   */
  async updateAsset(
    assetId: string,
    updates: { title?: string; summary?: string }
  ): Promise<ApiResponse<KnowledgeAsset>> {
    return apiClient.patch<KnowledgeAsset>(`${this.baseEndpoint}/${assetId}`, updates);
  }

  /**
   * Delete an asset and its storage file
   */
  async deleteAsset(assetId: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`${this.baseEndpoint}/${assetId}`);
  }

  /**
   * Re-process a URL asset to refresh its content
   */
  async refreshAsset(assetId: string): Promise<ApiResponse<KnowledgeAsset>> {
    return apiClient.post<KnowledgeAsset>(`${this.baseEndpoint}/${assetId}/refresh`);
  }
}

const knowledgeAssetService = new KnowledgeAssetService();
export default knowledgeAssetService;
